/**
 * useMapSelection — Sprint 4.1.2.
 *
 * Hook que gerencia modo de seleção espacial no Fleet Map:
 *   - retângulo (SHIFT + arrastar)
 *   - círculo   (ALT   + arrastar — clique=centro, drag=raio)
 *   - lasso     (tecla L; cliques marcam vértices; dblclick/Enter fecha)
 *
 * Comportamento:
 *   - Modo só é ativado por toolbar/teclado. Click/drag normais não disparam.
 *   - SHIFT durante o início de uma seleção = ACUMULATIVO (adiciona à seleção existente).
 *   - ESC cancela o modo atual (volta a navegar o mapa normalmente).
 *
 * Hit-test:
 *   - Retângulo/círculo: pixel-space via map.latLngToContainerPoint(...)
 *     (mais rápido + visual-correto em zoom alto).
 *   - Lasso: ray-casting em lat/lng (algoritmo Jordan curve).
 *
 * Output: getDevicesInside(devices) — lista filtrada pelos que caem
 * dentro da geometria desenhada (após `finalize`).
 *
 * Cor neon: #2BD9F0 (cyan-400), fill 20%, stroke 100%, dash durante draw.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const NEON = "#2BD9F0";
const FILL_OPACITY = 0.18;

/**
 * Point-in-polygon (ray-casting). Polígono = [{lat,lng}, ...].
 * Lat/Lng plano é suficiente em áreas pequenas (bairro/cidade).
 */
function pointInPolygon(lat, lng, polygon) {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].lng, yi = polygon[i].lat;
    const xj = polygon[j].lng, yj = polygon[j].lat;
    const intersect =
      yi > lat !== yj > lat &&
      lng < (xj - xi) * (lat - yi) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * @param {object} params
 * @param {object} params.L            — Leaflet instance
 * @param {object} params.mapRef       — useRef ao map
 * @param {function} params.onPreview  — opcional, recebe count durante draw
 * @param {function} params.getDevices — () => Device[] (para preview count)
 */
export function useMapSelection({ L, mapRef, onPreview, getDevices } = {}) {
  // modes: 'idle' | 'rectangle' | 'circle' | 'lasso'
  const [mode, setMode] = useState("idle");
  const [drawing, setDrawing] = useState(false);

  // Layer atualmente desenhada (preview ou finalizada)
  const layerRef = useRef(null);
  // Polígono em construção (lasso): array de {lat,lng}
  const lassoPointsRef = useRef([]);
  // Modificador no início do gesto (SHIFT = aditivo)
  const additiveRef = useRef(false);
  // Posição inicial do drag (em pixels da container) p/ rect/circle
  const startPxRef = useRef(null);

  const clearLayer = useCallback(() => {
    const map = mapRef?.current;
    if (map && layerRef.current) {
      map.removeLayer(layerRef.current);
    }
    layerRef.current = null;
  }, [mapRef]);

  const cancelMode = useCallback(() => {
    clearLayer();
    lassoPointsRef.current = [];
    setDrawing(false);
    setMode("idle");
    if (onPreview) onPreview({ count: 0, mode: "idle" });
  }, [clearLayer, onPreview]);

  // ---- Hit-test a partir da layer atual ----
  const getDevicesInsideLayer = useCallback((devices) => {
    const map = mapRef?.current;
    const layer = layerRef.current;
    if (!map || !layer) return [];

    // Caso lasso: temos um L.Polygon
    if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
      const poly = layer.getLatLngs()[0].map((p) => ({ lat: p.lat, lng: p.lng }));
      if (poly.length < 3) return [];
      return devices.filter(
        (d) =>
          d.lat != null && d.lng != null &&
          pointInPolygon(d.lat, d.lng, poly)
      );
    }

    // Retângulo: L.Rectangle → contains via bounds.contains
    if (layer instanceof L.Rectangle) {
      const bounds = layer.getBounds();
      return devices.filter(
        (d) => d.lat != null && d.lng != null && bounds.contains([d.lat, d.lng])
      );
    }

    // Círculo: distância em pixels (mais correto visualmente em zoom alto)
    if (layer instanceof L.Circle) {
      const center = layer.getLatLng();
      const radiusM = layer.getRadius();
      return devices.filter((d) => {
        if (d.lat == null || d.lng == null) return false;
        const dm = map.distance([d.lat, d.lng], center);
        return dm <= radiusM;
      });
    }

    return [];
  }, [L, mapRef]);

  // ---- Handler de tecla global ----
  useEffect(() => {
    const onKey = (e) => {
      // Ignora se foco está em input/textarea
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;

      if (e.key === "Escape") {
        if (mode !== "idle") {
          e.preventDefault();
          cancelMode();
        }
      } else if (e.key === "l" || e.key === "L") {
        if (mode === "idle" || mode === "lasso") {
          e.preventDefault();
          if (mode === "lasso") {
            cancelMode();
          } else {
            additiveRef.current = !!e.shiftKey;
            setMode("lasso");
            lassoPointsRef.current = [];
          }
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mode, cancelMode]);

  // ---- Handlers do mapa para rect/circle/lasso ----
  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !L) return;

    // Helper: descobre o modo correto a partir do mouseDown
    const detectModeFromMouse = (e) => {
      const orig = e.originalEvent || {};
      if (orig.shiftKey) return "rectangle";
      if (orig.altKey) return "circle";
      return null;
    };

    const onMouseDown = (e) => {
      const orig = e.originalEvent || {};
      // Atalhos por tecla pressionada DURANTE click
      if (mode === "idle") {
        const detected = detectModeFromMouse(e);
        if (!detected) return;
        // Importante: previne pan padrão do mapa
        map.dragging.disable();
        additiveRef.current = !!orig.shiftKey && detected !== "rectangle";
        // SHIFT+arrastar = retângulo; SHIFT+lasso/circle = aditivo
        setMode(detected);
        startPxRef.current = e.containerPoint;
        // Cria layer preview
        if (detected === "rectangle") {
          const ll = e.latlng;
          layerRef.current = L.rectangle(
            [[ll.lat, ll.lat], [ll.lng, ll.lng]],
            { color: NEON, weight: 2, dashArray: "6,4",
              fillColor: NEON, fillOpacity: FILL_OPACITY, interactive: false }
          ).addTo(map);
        } else if (detected === "circle") {
          layerRef.current = L.circle(e.latlng, {
            radius: 1, color: NEON, weight: 2, dashArray: "6,4",
            fillColor: NEON, fillOpacity: FILL_OPACITY, interactive: false,
          }).addTo(map);
        }
        setDrawing(true);
        return;
      }

      // Modo lasso: cliques marcam vértices
      if (mode === "lasso") {
        const ll = e.latlng;
        lassoPointsRef.current.push({ lat: ll.lat, lng: ll.lng });
        // Atualiza layer (polyline visível enquanto desenha; polygon ao fechar)
        if (lassoPointsRef.current.length === 1) {
          layerRef.current = L.polyline([[ll.lat, ll.lng]], {
            color: NEON, weight: 2, dashArray: "6,4", interactive: false,
          }).addTo(map);
        } else {
          layerRef.current.setLatLngs(
            lassoPointsRef.current.map((p) => [p.lat, p.lng])
          );
        }
        // Preview count enquanto desenha lasso (a partir de 3 pts)
        if (lassoPointsRef.current.length >= 3 && onPreview && getDevices) {
          const tempPoly = lassoPointsRef.current;
          const count = getDevices().filter(
            (d) => d.lat != null && d.lng != null
              && pointInPolygon(d.lat, d.lng, tempPoly)
          ).length;
          onPreview({ count, mode: "lasso", drawing: true });
        }
      }
    };

    const onMouseMove = (e) => {
      if (!drawing || !layerRef.current) return;
      if (mode === "rectangle") {
        const start = startPxRef.current;
        if (!start) return;
        const startLL = map.containerPointToLatLng(start);
        const endLL = e.latlng;
        layerRef.current.setBounds([
          [startLL.lat, startLL.lng], [endLL.lat, endLL.lng]
        ]);
        // Preview count
        if (onPreview && getDevices) {
          const bounds = layerRef.current.getBounds();
          const count = getDevices().filter(
            (d) => d.lat != null && d.lng != null && bounds.contains([d.lat, d.lng])
          ).length;
          onPreview({ count, mode: "rectangle", drawing: true });
        }
      } else if (mode === "circle") {
        const center = layerRef.current.getLatLng();
        const r = map.distance(center, e.latlng);
        layerRef.current.setRadius(r);
        if (onPreview && getDevices) {
          const count = getDevices().filter((d) => {
            if (d.lat == null || d.lng == null) return false;
            return map.distance([d.lat, d.lng], center) <= r;
          }).length;
          onPreview({ count, mode: "circle", drawing: true });
        }
      }
    };

    const onMouseUp = (e) => {
      if (!drawing) return;
      if (mode === "rectangle" || mode === "circle") {
        setDrawing(false);
        // Solidifica estilo: sem dash
        if (layerRef.current?.setStyle) {
          layerRef.current.setStyle({ dashArray: null });
        }
        map.dragging.enable();
        // Emite evento de finalize via key "ipwd:selection-finalize"
        document.dispatchEvent(new CustomEvent("ipwd:selection-finalize", {
          detail: { mode, additive: additiveRef.current },
        }));
        // Volta para idle (modo é one-shot)
        setMode("idle");
      }
    };

    const onDblClick = () => {
      if (mode !== "lasso") return;
      if (lassoPointsRef.current.length < 3) return;
      // Fecha: troca polyline por polygon
      if (layerRef.current) map.removeLayer(layerRef.current);
      layerRef.current = L.polygon(
        lassoPointsRef.current.map((p) => [p.lat, p.lng]),
        { color: NEON, weight: 2, fillColor: NEON, fillOpacity: FILL_OPACITY,
          interactive: false }
      ).addTo(map);
      lassoPointsRef.current = [];
      document.dispatchEvent(new CustomEvent("ipwd:selection-finalize", {
        detail: { mode: "lasso", additive: additiveRef.current },
      }));
      setMode("idle");
    };

    const onEnter = (e) => {
      if (e.key === "Enter" && mode === "lasso") {
        if (lassoPointsRef.current.length >= 3) onDblClick();
      }
    };

    map.on("mousedown", onMouseDown);
    map.on("mousemove", onMouseMove);
    map.on("mouseup", onMouseUp);
    map.on("dblclick", onDblClick);
    document.addEventListener("keydown", onEnter);

    return () => {
      map.off("mousedown", onMouseDown);
      map.off("mousemove", onMouseMove);
      map.off("mouseup", onMouseUp);
      map.off("dblclick", onDblClick);
      document.removeEventListener("keydown", onEnter);
    };
  }, [L, mapRef, mode, drawing, onPreview, getDevices]);

  /**
   * Inicia modo programaticamente (por botão da toolbar).
   * additive=true mantém seleção atual; false zera.
   */
  const startMode = useCallback((newMode, additive = false) => {
    cancelMode();
    additiveRef.current = additive;
    if (newMode === "lasso") {
      lassoPointsRef.current = [];
      setMode("lasso");
    } else if (newMode === "rectangle" || newMode === "circle") {
      // Esses iniciam quando o usuário começa a arrastar — mostramos
      // hint visual mas ficam em "idle" até o mousedown.
      // Workaround: forçar com tecla virtual setando modo já;
      // o mousedown vai pegar como rectangle/circle.
      // Pra UX coerente, melhor exigir SHIFT/ALT mesmo se acionado por botão.
      setMode("idle");
    }
  }, [cancelMode]);

  // ---- Sprint 4.2C — exporta polígono desenhado como GeoJSON ----
  const getLastDrawnGeoJson = useCallback(() => {
    const layer = layerRef.current;
    if (!layer) return null;
    try {
      // Para Polygon/Rectangle: toGeoJSON() devolve Feature; queremos só a geometry
      if (layer instanceof L.Polygon) {
        const gj = layer.toGeoJSON();
        return gj?.geometry || null;
      }
      // Para Circle: aproximamos como Polygon de 64 vértices
      if (layer instanceof L.Circle) {
        const map = mapRef?.current;
        const center = layer.getLatLng();
        const radiusM = layer.getRadius();
        if (!map || !radiusM) return null;
        const N = 64;
        const ring = [];
        for (let i = 0; i < N; i++) {
          const ang = (i / N) * 2 * Math.PI;
          // Aproximação plana (OK para áreas pequenas, <50km)
          const dLat = (radiusM / 111320) * Math.sin(ang);
          const dLng = (radiusM / (111320 * Math.cos(center.lat * Math.PI / 180))) * Math.cos(ang);
          ring.push([center.lng + dLng, center.lat + dLat]);
        }
        ring.push([...ring[0]]); // fecha
        return { type: "Polygon", coordinates: [ring] };
      }
    } catch {
      return null;
    }
    return null;
  }, [L, mapRef]);

  return {
    mode,
    drawing,
    startMode,
    cancelMode,
    getDevicesInsideLayer,
    getLastDrawnGeoJson,
    additiveRef,
  };
}
